import { Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useEvents } from "api/events";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { OLCheckbox } from "components/FormControls/OLCheckbox";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    grayText: {
      color: theme.gray500,
      display: "block",
      fontSize: 18,
      margin: theme.spacing(12, 0, 8),
    },
    input: { width: "100%" },
  }),
);

interface Props {
  customEvent?: {
    type?: string;
    body?: any;
  };
}

export function EventCampaignSimulation({ customEvent }: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { useInfiniteCustomEventsSchemaList } = useEvents();
  const [eventTypeSearch, setEventTypeSearch] = useState<string>();

  const {
    data: infiniteCustomEventSchemaList,
    size: customEventSchemaSize,
    setSize: setCustomEventSchemaSize,
    isValidating,
  } = useInfiniteCustomEventsSchemaList({
    filters: eventTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: eventTypeSearch,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const {
    infiniteData: customEventSchemaList,
    anyElementsToFetch: anyCustomEventSchemaToFetch,
  } = useManageInfiniteScroll(infiniteCustomEventSchemaList || []);

  const isSchemaAllowed = ({ schema }: { schema: any }) => {
    const allowedFieldTypes = ["boolean", "datetime", "number", "text"];
    return schema?.fields?.every(({ type }: { type: string }) =>
      allowedFieldTypes.includes(type),
    );
  };
  const generateAllowedSchemas = () =>
    customEventSchemaList.filter(isSchemaAllowed);

  const allowedSchemas = generateAllowedSchemas();

  return (
    <Grid container spacing={10}>
      <Grid xs={4} item>
        <SelectInput
          displaySearch
          data-test-id="campaign-simulator-custom-event-type-select"
          width="100%"
          items={
            allowedSchemas?.map(
              (event: { eventType: string; name: string }) => ({
                label: event.name,
                id: event.eventType,
              }),
            ) || []
          }
          label={<RequiredFieldLabel text={message({ id: "eventType" })} />}
          name="custom_event.type"
          asyncSearch={(searchedQuery: string) =>
            setEventTypeSearch(searchedQuery)
          }
          placeholder={message({
            id: "eventType",
          })}
          anyElementsToFetch={anyCustomEventSchemaToFetch}
          loadMoreElements={() =>
            !isValidating && setCustomEventSchemaSize(customEventSchemaSize + 1)
          }
        />
      </Grid>
      <Grid xs={4} item>
        <DateTimeInput
          width="100%"
          defaultValue={null}
          format="yyyy-MM-dd HH:mmXXX"
          label={<RequiredFieldLabel text={message({ id: "eventDate" })} />}
          name="custom_event.eventDate"
          data-test-id="campaign-simulator"
        />
      </Grid>
      {customEvent?.type &&
        allowedSchemas &&
        allowedSchemas
          .find((schema) => schema.eventType === customEvent.type)
          ?.schema.fields.map((item, index) => (
            <Grid xs={4} item key={index}>
              {(item.type === "boolean" && (
                <OLCheckbox
                  label={item.description || item.name}
                  name={`custom_event.body.${item.name}`}
                  data-test-id={`campaign-simulator-schema-checkbox-${index}`}
                />
              )) ||
                (item.type === "datetime" && (
                  <DateTimeInput
                    width="100%"
                    format="yyyy-MM-dd HH:mmXXX"
                    label={item.description || item.name}
                    name={`custom_event.body.${item.name}`}
                    data-test-id={`campaign-simulator-schema-datetime-${index}`}
                  />
                )) ||
                (item.type === "number" && (
                  <TextInput
                    className={classes.input}
                    inputProps={{ step: "any" }}
                    label={item.description || item.name}
                    name={`custom_event.body.${item.name}`}
                    type="number"
                    data-test-id={`campaign-simulator-schema-number-${index}`}
                  />
                )) ||
                (item.type === "text" && (
                  <TextInput
                    className={classes.input}
                    label={item.description || item.name}
                    name={`custom_event.body.${item.name}`}
                    data-test-id={`campaign-simulator-schema-text-${index}`}
                  />
                ))}
            </Grid>
          ))}
    </Grid>
  );
}
